export type JwtPayload = Record<string, unknown>;

function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  const pad = normalized.length % 4;
  const padded = pad ? normalized.padEnd(normalized.length + (4 - pad), "=") : normalized;

  if (typeof atob === "function") {
    return atob(padded);
  }

  const globalBuffer =
    typeof globalThis !== "undefined" ? (globalThis as { Buffer?: { from(input: string, encoding: string): { toString(encoding: string): string } } }).Buffer : undefined;

  if (globalBuffer) {
    return globalBuffer.from(padded, "base64").toString("binary");
  }

  throw new Error("Unable to decode JWT payload");
}

export function decodeJwtPayload<T extends JwtPayload = JwtPayload>(token: string): T | null {
  if (!token) {
    return null;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

export function readJwtIssuedAt(payload: JwtPayload | null): string | null {
  if (!payload) {
    return null;
  }

  const iat = payload.iat;
  if (typeof iat !== "number") {
    return null;
  }

  const timestamp = iat * 1000;
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return new Date(timestamp).toISOString();
}

export function readJwtExpiry(payload: JwtPayload | null): string | null {
  if (!payload) {
    return null;
  }

  const exp = payload.exp;
  if (typeof exp !== "number") {
    return null;
  }

  const timestamp = exp * 1000;
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return new Date(timestamp).toISOString();
}
